interface QuestionOption {
  id: number;
}

interface HotspotProps {
  type?: string;
  options?: QuestionOption[];
  states?: number;
  currentState?: number;
  [key: string]: unknown;
}

interface Hotspot {
  props: HotspotProps;
  [key: string]: unknown;
}

/**
 * State a question hotspot holds before anything has been submitted. Options map
 * onto the states above it, so the first option is state 2.
 */
export const UNANSWERED_STATE = 1;

const STATEFUL_QUESTION_TYPES = ["CSCQ"];

/**
 * State a question hotspot takes once `optionId` has been submitted.
 *
 * Options are addressed by their position, so the mapping stays stable no matter
 * what ids the authoring tool assigns (they are neither sequential nor
 * zero-based). Returns null when the option is unknown, so a stale submission
 * leaves the hotspot's state untouched rather than moving it somewhere arbitrary.
 */
export const getStateForSubmittedOption = (
  options: QuestionOption[] | undefined,
  optionId: number | undefined
): number | null => {
  if (!Array.isArray(options) || optionId === undefined) return null;

  const position = options.findIndex((option) => option.id === optionId);
  if (position === -1) return null;

  return position + UNANSWERED_STATE + 1;
};

/**
 * Gives every question hotspot the same `states`/`currentState` pair a
 * state-changer carries, so the existing dependency checks — which read
 * `props.currentState` without caring about the hotspot's type — can condition on
 * a submitted answer.
 *
 * An already-set `currentState` is preserved: hotspots are persisted and handed
 * back through `sceneData` when a learner returns to a section, and their answer
 * has to survive that round trip.
 */
export const withDerivedQuestionStates = <T extends Record<string, Hotspot>>(
  hotspots: T
): T => {
  const derived = { ...hotspots } as Record<string, Hotspot>;

  Object.keys(derived).forEach((id) => {
    const hotspot = derived[id];
    const hotspotProps = hotspot?.props;

    if (
      !hotspotProps ||
      !STATEFUL_QUESTION_TYPES.includes(hotspotProps.type as string) ||
      !Array.isArray(hotspotProps.options)
    ) {
      return;
    }

    derived[id] = {
      ...hotspot,
      props: {
        ...hotspotProps,
        states: hotspotProps.options.length + UNANSWERED_STATE,
        currentState: hotspotProps.currentState ?? UNANSWERED_STATE,
      },
    };
  });

  return derived as T;
};
